import { Fragment, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { Edit, ThumbnailUnknownObject16x9 } from '@sas/react-svg';
import cx from 'classnames';
import { setAlpha } from '../../../design-tokens';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { ContextMenu } from '../../ContextMenu';
import { Icon } from '../../Icon';
import { Image } from '../../Image';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { MessageDialog } from '../../MessageDialog';
import { SplitButton } from '../../SplitButton';
import { SVG } from '../../SVG';
import { CropperDialog } from './CropperDialog';
import { useImageSelectorA11yInfo } from './useImageSelectorA11yInfo';
import bundle from '../NovaComponents-ImageSelector-gui-icu.properties';
import cn from './BaseImageSelector.css.json';
import './BaseImageSelector.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultAspectRatio = 16 / 9;
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHintTextHtmlId = createUseHtmlId('hint-text');
const normalizeImageTypes = imageTypes => {
  // JPG and JPEG always come together because they are actually the same type
  if (!imageTypes.includes('jpg') && !imageTypes.includes('jpeg')) return [...imageTypes];
  return [...imageTypes.filter(type => type !== 'jpg' && type !== 'jpeg'), 'jpg', 'jpeg'];
};

/**
 * @internal
 */
export const BaseImageSelector = props => {
  const localize = useTextLocalizer();
  const hintTextIds = useHintTextHtmlId(3);
  const ariaDescribedBy = props.showHintText ? props.additionalHintText ? hintTextIds : hintTextIds.slice(0, 2) : undefined;
  const resultImageType = props.resultImageType ?? 'jpg';
  const maxFileSize = props.maxFileSize ?? 1;
  const validImageTypes = normalizeImageTypes(props.validImageTypes ?? ['jpg', 'jpeg', 'png', 'bmp', 'gif']);
  const imageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isBusy, setIsBusy] = useState(false);
  const [cropperDialogSrc, setCropperDialogSrc] = useState();
  const [messageDialogProps, setMessageDialogProps] = useState();
  const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
  const [isHotSpotActive, setIsHotSpotActive] = useState(false);
  const prevFocusedElementRef = useRef();
  const splitButtonRef = useRef(null);
  const isBusyRef = useRef(false);
  const showDefaultImage = !props.src || props.src.length === 0;
  const imageSrc = (showDefaultImage ? props.defaultSrc : props.src) ?? '';
  const onImageSrcError = () => {
    setCropperDialogSrc(undefined);
    if (props.onFileValidationError) {
      props.onFileValidationError({
        reason: 'failedToLoad'
      });
    } else {
      setMessageDialogProps({
        isOpen: true,
        type: 'error',
        title: localize('chooseImage.invalidFileContent.error.dialog.title'),
        text: localize('chooseImage.invalidFileContent.error.dialog.content.msg'),
        dismissAction: {
          text: localize('chooseImage.invalidFileContent.error.dialog.dismissButton.label'),
          onPress: () => {
            setMessageDialogProps(undefined);
            // Wait for the message dialog to close before re-opening the file selector
            // so that the focus can be set back to the ImageSelector
            setTimeout(onChooseImage, 0);
          },
          isDefault: true
        }
      });
    }
  };
  const onChooseImage = async () => {
    prevFocusedElementRef.current?.focus();
    if (props.onChooseImage) {
      props.onChooseImage().then(({
        type,
        size,
        src
      }) => {
        if (!isValidFile(type, size)) return;
        setCropperDialogSrc(src);
      }).catch(onImageSrcError);
    } else {
      fileInputRef.current?.click();
    }
  };
  const onRemoveImage = () => {
    prevFocusedElementRef.current?.focus();
    const removeErrorMessageDialogProps = {
      isOpen: true,
      type: 'error',
      title: localize('removeImage.error.dialog.title'),
      text: localize('removeImage.error.dialog.content.msg'),
      dismissAction: {
        text: localize('removeImage.error.dialog.dismissButton.label'),
        onPress: () => {
          setMessageDialogProps(undefined);
        },
        isDefault: true
      }
    };
    if (!props.skipRemovingImageWarning) {
      setMessageDialogProps({
        type: 'warning',
        isOpen: true,
        title: localize('removeImage.warning.dialog.title'),
        text: localize('removeImage.warning.dialog.content.msg'),
        firstAction: {
          text: localize('removeImage.warning.dialog.confirmButton.label'),
          onPress: () => {
            setMessageDialogProps(undefined);
            props.onRemoveImage?.().catch(() => setMessageDialogProps(removeErrorMessageDialogProps));
          }
        },
        dismissAction: {
          text: localize('removeImage.warning.dialog.dismissButton.label'),
          onPress: () => {
            setMessageDialogProps(undefined);
          },
          isDefault: true
        }
      });
    } else {
      props.onRemoveImage?.().catch(() => setMessageDialogProps(removeErrorMessageDialogProps));
    }
  };
  const onCrop = croppedCanvas => {
    // Check isBusyRef.current instead of isBusy because the state updating is asynchronous
    if (!props.onSaveImage || isBusyRef.current) return;
    isBusyRef.current = true;
    setIsBusy(true);
    props.onSaveImage(croppedCanvas.toDataURL(`image/${resultImageType}`)).then(() => {
      setCropperDialogSrc(undefined);
    }).catch(() => {
      setMessageDialogProps({
        isOpen: true,
        type: 'error',
        title: localize('saveImage.error.dialog.title'),
        text: localize('saveImage.error.dialog.content.msg'),
        dismissAction: {
          text: localize('saveImage.error.dialog.dismissButton.label'),
          onPress: () => {
            setMessageDialogProps(undefined);
          },
          isDefault: true
        }
      });
    }).finally(() => {
      isBusyRef.current = false;
      setIsBusy(false);
    });
  };
  const isValidFile = (fileType, fileSize) => {
    if (!validImageTypes.map(type => `image/${type}`).includes(fileType)) {
      if (props.onFileValidationError) {
        props.onFileValidationError({
          reason: 'wrongType'
        });
      } else {
        setMessageDialogProps({
          isOpen: true,
          type: 'error',
          title: localize('chooseImage.invalidFileType.error.dialog.title'),
          text: localize('chooseImage.invalidFileType.error.dialog.content.msg', {
            validFileTypes: getValidFileTypesString(validImageTypes)
          }),
          dismissAction: {
            text: localize('chooseImage.invalidFileType.error.dialog.dismissButton.label'),
            onPress: () => {
              onChooseImage();
              setMessageDialogProps(undefined);
            },
            isDefault: true
          }
        });
      }
      return false;
    }
    if (fileSize >= maxFileSize * 1048576 /* MB */) {
      if (props.onFileValidationError) {
        props.onFileValidationError({
          reason: 'sizeTooLarge'
        });
      } else {
        setMessageDialogProps({
          isOpen: true,
          type: 'error',
          title: localize('chooseImage.invalidFileSize.error.dialog.title'),
          text: localize('chooseImage.invalidFileSize.error.dialog.content.msg', {
            maxFileSize: maxFileSize.toString()
          }),
          dismissAction: {
            text: localize('chooseImage.invalidFileSize.error.dialog.dismissButton.label'),
            onPress: () => {
              onChooseImage();
              setMessageDialogProps(undefined);
            },
            isDefault: true
          }
        });
      }
      return false;
    }
    return true;
  };
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars({
      'font-size-s': dt.tokens['font-size-body-text-s'],
      overlay: setAlpha(dt.tokens['color-container-background-0'], 0.6),
      bg: dt.tokens['color-container-background-2'],
      'anim-dur': dt.tokens['duration-100'],
      'anim-tf': dt.tokens['ease-in-out'],
      'edit-icon-bd': dt.tokens['color-primary'],
      'edit-icon-bg': dt.tokens['color-action-icon-control-normal']
    }, 'image-selector')
  };
  const getImageSelectorA11yInfo = useImageSelectorA11yInfo();
  const {
    tooltip,
    ariaLabel
  } = getImageSelectorA11yInfo({
    tooltip: props.tooltip,
    ariaLabel: props.ariaLabel
  });
  const {
    width: imageWidth,
    height: imageHeight
  } = getSize(props.imageSize);
  const [setResizeObserverRef] = useResizeObserverRef(() => {
    if (!imageContainerRef.current) return;
    const {
      width,
      height
    } = imageContainerRef.current.getBoundingClientRect();
    if (width === 0 || height === 0 || Math.abs(width / height - defaultAspectRatio) < 0.01) {
      setAspectRatio(defaultAspectRatio);
    } else {
      setAspectRatio(width / height);
    }
  });
  const setImageContainerRef = element => {
    imageContainerRef.current = element;
    setResizeObserverRef(element);
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(Flow, {
      spacing: "m",
      orientation: "vertical",
      style: tokens,
      children: [/*#__PURE__*/_jsx(ContextMenu, {
        sections: props.disableContextMenu ? undefined : [{
          items: [{
            text: localize('contextMenu.chooseImage.menuItem.label')
          }, {
            text: localize('contextMenu.removeImage.menuItem.label'),
            disabled: showDefaultImage
          }]
        }],
        onItemSelected: indices => {
          if (indices[0][1] === 0) {
            onChooseImage();
          } else {
            onRemoveImage();
          }
        },
        children: !props.renderImageContainer ? /*#__PURE__*/_jsxs("div", {
          "data-testid": postfixTestId(props.testId, 'imageContainer'),
          ref: setImageContainerRef,
          className: cx(cn.imageContainer, aspectRatio !== defaultAspectRatio && !props.defaultSrc && !props.src && cn.hasBackgroundColor),
          tabIndex: 0,
          role: "button",
          onClick: onChooseImage,
          onKeyDown: e => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setIsHotSpotActive(true);
            }
          },
          onKeyUp: e => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setIsHotSpotActive(false);
              onChooseImage();
            }
          },
          style: {
            width: imageWidth,
            height: imageHeight
          },
          title: tooltip,
          "aria-label": ariaLabel,
          onFocus: ({
            currentTarget
          }) => {
            prevFocusedElementRef.current = currentTarget;
          },
          "aria-describedby": ariaDescribedBy?.join(' '),
          children: [/*#__PURE__*/_jsx("div", {
            "aria-hidden": "true",
            children: imageSrc.length > 0 ? /*#__PURE__*/_jsx(Image, {
              src: imageSrc,
              mode: "fit",
              className: cn.image
            }) : aspectRatio === defaultAspectRatio ? /*#__PURE__*/_jsx(SVG, {
              icon: ThumbnailUnknownObject16x9,
              type: "thumbnail"
            }) : null
          }), /*#__PURE__*/_jsx("div", {
            className: cx(cn.overlay, isHotSpotActive && cn.active),
            children: /*#__PURE__*/_jsx("div", {
              className: cn.editIcon,
              children: /*#__PURE__*/_jsx(Icon, {
                icon: Edit,
                tokens: () => {
                  return {
                    fg: dt.tokens['color-text-neutral-inverse']
                  };
                },
                tooltip: tooltip,
                ariaHidden: true
              })
            })
          })]
        }) : props.renderImageContainer({
          ref: setImageContainerRef,
          onPress: onChooseImage,
          tooltip,
          onFocus: element => {
            prevFocusedElementRef.current = element;
          }
        })
      }), props.showSplitButton ? /*#__PURE__*/_jsx("div", {
        onFocus: () => {
          if (splitButtonRef.current) {
            prevFocusedElementRef.current = splitButtonRef.current;
          }
        },
        children: /*#__PURE__*/_jsx(SplitButton, {
          testId: postfixTestId(props.testId, 'splitButton'),
          ref: splitButtonRef,
          text: localize('splitButton.label'),
          items: [localize('splitButton.chooseImage.menuItem.label'), {
            text: localize('splitButton.removeImage.menuItem.label'),
            disabled: showDefaultImage
          }],
          onPress: onChooseImage,
          onItemSelected: index => {
            if (index === 0) onChooseImage();else onRemoveImage();
          },
          ariaDescribedBy: ariaDescribedBy
        })
      }) : null, props.showHintText ? /*#__PURE__*/_jsxs(Flow, {
        spacing: "xs",
        orientation: "vertical",
        children: [/*#__PURE__*/_jsx(Label, {
          className: cn.hintText,
          id: hintTextIds[0],
          children: localize('maximumFileSize.hint.msg', {
            maxFileSize: (props.maxFileSize ?? 1).toString()
          })
        }), /*#__PURE__*/_jsx(Label, {
          className: cn.hintText,
          id: hintTextIds[1],
          children: localize('validFileTypes.hint.msg', {
            validFileTypes: getValidFileTypesString(validImageTypes)
          })
        }), props.additionalHintText ? /*#__PURE__*/_jsx(Label, {
          className: cn.hintText,
          id: hintTextIds[2],
          children: props.additionalHintText
        }) : null]
      }) : null]
    }), /*#__PURE__*/_jsx(CropperDialog, {
      testId: postfixTestId(props.testId, 'cropperDialog'),
      isBusy: isBusy,
      ellipse: props.ellipse,
      aspectRatio: aspectRatio,
      isOpen: !!cropperDialogSrc,
      src: cropperDialogSrc ?? '',
      onCrop: onCrop,
      onDismiss: () => {
        setMessageDialogProps({
          isOpen: true,
          type: 'warning',
          title: localize('cropImage.unsavedChanges.warning.dialog.title'),
          text: localize('cropImage.unsavedChanges.warning.dialog.content.msg'),
          firstAction: {
            text: localize('cropImage.unsavedChanges.warning.dialog.confirmButton.label'),
            onPress: () => {
              setCropperDialogSrc(undefined);
              setMessageDialogProps(undefined);
            }
          },
          dismissAction: {
            text: localize('cropImage.unsavedChanges.warning.dialog.dismissButton.label'),
            onPress: () => {
              setMessageDialogProps(undefined);
            },
            isDefault: true
          }
        });
      },
      onError: onImageSrcError
    }), /*#__PURE__*/_jsx("input", {
      type: "file",
      accept: validImageTypes.map(type => `.${type}`).join(','),
      ref: fileInputRef,
      value: "" // To make sure whenver a file is selected, onChange is fired
      ,
      onChange: event => {
        const files = event.target.files;
        if (files?.length !== 1) return;
        const file = files[0];
        if (!isValidFile(file.type, file.size)) return;
        const reader = new FileReader();
        reader.onload = e => setCropperDialogSrc(e.target?.result?.toString());
        reader.readAsDataURL(file);
      },
      hidden: true
    }), messageDialogProps ? /*#__PURE__*/_jsx(MessageDialog, {
      ...messageDialogProps
    }) : null]
  });
};
const getValidFileTypesString = validImageTypes => {
  return [...validImageTypes].sort().join(', ').toUpperCase();
};
const getSize = size => {
  const {
    width,
    height
  } = size;
  const hasCustomWidth = !isNullish(width) && width.toString().length > 0;
  const hasCustomHeight = !isNullish(height) && height.toString().length > 0;
  if (hasCustomWidth && hasCustomHeight) {
    return {
      width,
      height
    };
  } else if (!hasCustomWidth && hasCustomHeight) {
    return typeof height === 'number' ? {
      width: height * defaultAspectRatio,
      height
    } : {
      width: `calc(${height} * ${defaultAspectRatio})`,
      height
    };
  } else if (hasCustomWidth && !hasCustomHeight) {
    return typeof width === 'number' ? {
      width,
      height: width / defaultAspectRatio
    } : {
      width,
      height: `calc(${width} / ${defaultAspectRatio})`
    };
  } else {
    // Dimension would be 0x0 if neither width nor height is provided
    return {
      width: 0,
      height: 0
    };
  }
};
//# sourceMappingURL=BaseImageSelector.js.map